import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '@/utils/pageTitle';
import { ChevronLeft, Home, Menu, X, Settings, TrendingUp, Loader2, LayoutDashboard, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePrimaryLedger } from './PrimaryLedgerContext';
import { useAuth } from '@/context/AuthContext';
import { employeeNamesService } from '@/services/employeeNames';
import { UserInfoSection } from '@/components/layout/UserInfoSection';
import KeyboardNavigableSelect from '@/components/ui/KeyboardNavigableSelect';
import { primaryLedgerService } from '@/services/primaryLedgerService';
import { useToast } from '@/components/ui/use-toast';

const PrimaryLedgerLayout: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pasteAsinInput, setPasteAsinInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const {
    companies,
    selectedCompany,
    selectedAsin,
    asins,
    isLoadingCompanies,
    isLoadingAsins,
    setSelectedCompany,
    setSelectedAsin,
  } = usePrimaryLedger();

  useEffect(() => {
    setPageTitle('Primary Ledger');
  }, []);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  const handlePasteASIN = async () => {
    if (!pasteAsinInput.trim()) return;

    const asin = pasteAsinInput.trim().toUpperCase();
    setIsSearching(true);

    try {
      const foundCompany = await primaryLedgerService.findCompanyByASIN(asin);
      
      if (foundCompany) {
        setSelectedCompany(foundCompany);
        setSelectedAsin(asin);
        setPasteAsinInput('');
        toast({
          title: 'ASIN Found',
          description: `Switched to ${foundCompany} - ${asin}`,
          duration: 3000
        });
      } else {
        toast({
          title: 'ASIN Not Found',
          description: 'ASIN not found in any company data',
          variant: 'destructive',
          duration: 4000
        });
      }
    } catch (error) {
      console.error('Error searching for ASIN:', error);
      toast({
        title: 'Search Error',
        description: 'Failed to search for ASIN',
        variant: 'destructive',
        duration: 4000
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handlePasteASINKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handlePasteASIN();
    }
  };

  const companyOptions = companies.map(company => ({
    value: company.id,
    label: company.companyName
  }));

  const asinOptions = asins.map(asin => ({ 
    value: asin, 
    label: asin 
  }));

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="border-b flex justify-center items-center h-23 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/primary-ledger')}
          title="Primary Ledger Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <TrendingUp className="h-6 w-6 text-purple-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-4">
              <TrendingUp className="h-10 w-10 text-purple-500 mb-1" />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Primary Ledger</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">ASIN Performance</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          <div className={`px-4 py-2 mb-4 space-y-2 ${isCollapsed && !isMobileMenuOpen ? 'hidden' : 'block'}`}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Select Company
              </label>
              <KeyboardNavigableSelect
                options={companyOptions}
                value={selectedCompany || ''}
                onValueChange={setSelectedCompany}
                placeholder={isLoadingCompanies ? "Loading..." : "Select a company"}
                className="w-full"
                triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                disabled={isLoadingCompanies}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                itemStyle={{ color: 'black' }}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Select ASIN
              </label>
              <KeyboardNavigableSelect
                options={asinOptions}
                value={selectedAsin || ''}
                onValueChange={setSelectedAsin}
                placeholder={!selectedCompany ? "Select a company first" : isLoadingAsins ? "Loading ASINs..." : "Select an ASIN"}
                className="w-full"
                triggerClassName="w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                disabled={!selectedCompany || isLoadingAsins}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                itemStyle={{ color: 'black' }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">
                Paste ASIN
              </label>
              <div className="flex gap-2">
                <Input
                  value={pasteAsinInput}
                  onChange={(e) => setPasteAsinInput(e.target.value)}
                  onKeyPress={handlePasteASINKeyPress}
                  placeholder="Paste ASIN to jump to company..."
                  disabled={isSearching}
                  className="text-sm"
                />
                <Button
                  onClick={handlePasteASIN}
                  disabled={!pasteAsinInput.trim() || isSearching}
                  size="sm"
                  className="px-3"
                >
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Go'
                  )}
                </Button>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <Button
                onClick={() => handleNavigate('/primary-ledger')}
                size="sm"
                className="w-full"
                variant="outline"
              >
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <Button
                onClick={() => handleNavigate('/primary-ledger/snapshots')}
                size="sm"
                className="w-full"
                variant="outline"
              >
                <Camera className="h-4 w-4 mr-2" />
                Snapshots
              </Button>
              <Button
                onClick={() => handleNavigate('/primary-ledger/manage-companies')}
                size="sm"
                className="w-full"
                variant="outline"
              >
                <Settings className="h-4 w-4 mr-2" />
                Manage Companies
              </Button>
            </div>
          </div>

          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        <UserInfoSection displayName={displayName} />
      </aside>

      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>
    </div>
  );
};

export default PrimaryLedgerLayout;